import { computed } from 'vue';
import { defineStore } from 'pinia';
import { config } from '@/config';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import { announceSessionChange } from '@/integrations/Immersve/auth/sessionChangeSignal';
import type { Whoami } from '@/integrations/Immersve/auth/types';
import { transientState } from '@/integrations/Immersve/auth/TransientState';
import { User } from '@/integrations/Immersve/auth/User';
import { persistedRef } from '@/util/persistedRef';

// 'not-applicable' is authType !== 'cookie': no session concept, not "logged out".
export type AuthStatus = 'not-applicable' | 'unknown' | 'authenticated' | 'unauthenticated';

export const useAuthStore = defineStore('auth', () => {
  const status = persistedRef<AuthStatus>(
    sessionStorage,
    'larimar.auth.status',
    config.authType === 'cookie' ? 'unknown' : 'not-applicable'
  );
  const whoami = persistedRef<Whoami | undefined>(sessionStorage, 'larimar.auth.whoami', undefined);
  const user = computed(() => User.fromWhoami(whoami.value));
  let initializePromise: Promise<void> | undefined;

  async function checkSession() {
    try {
      const identity = await useImmersveApiClient().getWhoami();
      whoami.value = identity;
      if (config.authType === 'cookie') {
        status.value = identity ? 'authenticated' : 'unauthenticated';
      }
    } catch {
      // A failing whoami isn't "logged out" — keep the last-known state.
    }
  }

  // Cached so route guards await the check App.vue kicked off, not a second fetch.
  function initialize(): Promise<void> {
    if (!initializePromise) {
      initializePromise = checkSession();
    }
    return initializePromise;
  }

  function refresh(): Promise<void> {
    initializePromise = checkSession();
    return initializePromise;
  }

  // The HttpOnly cookie can't be cleared client-side, so a failed logout re-validates.
  async function logout(): Promise<boolean> {
    try {
      await useImmersveApiClient().postLogout();
    } catch {
      await refresh();
      return false;
    }
    status.value = 'unauthenticated';
    whoami.value = undefined;
    announceSessionChange();
    transientState.clearReturnTo();
    return true;
  }

  return { status, whoami, user, initialize, refresh, logout };
});
